import {
  Card,
  Divider,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { WorkspaceUser } from 'src/@types/user';
import { routes } from 'src/Router';
import { useAuthContext } from 'src/auth/useAuthContext';
import { CustomAvatar } from 'src/components/custom-avatar';
import { useLocales } from 'src/locales';
import { ChatRequests } from 'src/requests/chat';
import { GuestChannelInfo } from 'src/requests/gathering';

type Props = {
  gathering: GuestChannelInfo;
  onSuccess(user: WorkspaceUser): void;
};

const ITEM_HEIGHT = 64;

export function JoinConferenceUserSelection({ gathering, onSuccess }: Props) {
  const { translate } = useLocales();
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const users = user?.myUsers || [];

  const handleClickAccount = async (user: WorkspaceUser) => {
    if (user.workspace._id === gathering.workspace) {
      const { slug } = user.workspace;
      const channel = await ChatRequests.getChannelById(gathering._id);
      if (channel) {
        navigate(`/${slug}${routes.dashboard.chat.view(gathering._id)}`);
        return;
      }
    }
    onSuccess(user);
  };

  const handleLogout = () => {
    logout(undefined, true);
  };

  return (
    <Card sx={{ width: '100%' }}>
      <Stack sx={{ p: 3 }}>
        <Typography variant="h5" textAlign="center" mb={2}>
          {translate('communicate.join_conference.user_logged_in_as', {
            name: user?.name || '',
          })}
        </Typography>

        <Typography variant="subtitle2" mb={1}>
          {translate('communicate.join_conference.user_selection_title')}
        </Typography>

        <Stack sx={{ width: '100%', maxHeight: '350px', overflowY: 'auto' }}>
          {users.map((myUser) => (
            <MenuItem
              key={myUser._id}
              sx={{ height: ITEM_HEIGHT }}
              onClick={() => handleClickAccount(myUser)}
            >
              <ListItemAvatar>
                <CustomAvatar
                  src={myUser.picture}
                  alt={myUser?.name}
                  name={myUser?.name}
                />
              </ListItemAvatar>

              <ListItemText
                primary={myUser.name}
                secondary={myUser.workspace.name}
                primaryTypographyProps={{
                  typography: 'subtitle2',
                  sx: { mb: 0.25 },
                }}
                secondaryTypographyProps={{ typography: 'caption' }}
              />
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }}> or </Divider>

        <StyledLogout
          variant="subtitle2"
          color="text.secondary"
          onClick={handleLogout}
        >
          {translate('communicate.join_conference.join_different_account')}
        </StyledLogout>
      </Stack>
    </Card>
  );
}

const StyledLogout = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));
